import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "~/atoms/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/atoms/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  isSeparator?: boolean;
}

interface BaseProps {
  disabled?: boolean;
  placeholder?: string;
  options?: Option[];
  showSearch?: boolean;
}

type SingleSelectProps = BaseProps & {
  isMulti?: false;
  value?: string | null;
  onChange: (value: string) => void;
};

type MultiSelectProps = BaseProps & {
  isMulti: true;
  value?: string[];
  onChange: (value: string[]) => void;
};

type Props = SingleSelectProps | MultiSelectProps;

const getMultiSelectLabel = (
  selectedOptions: Option[],
  fallbackLabel: string,
): string => {
  if (selectedOptions.length === 0) {
    return fallbackLabel;
  }
  const firstOption = selectedOptions[0];
  if (!firstOption) {
    return fallbackLabel;
  }
  if (selectedOptions.length === 1) {
    return firstOption.label;
  }
  return `${firstOption.label} +${selectedOptions.length - 1}`;
};

export const ControlledCombobox = ({ ...props }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const fallbackLabel = props.placeholder ?? t(($) => $.commons.select);
  const multiValue = props.isMulti ? (props.value ?? []) : [];
  const selectedValues = new Set(multiValue);
  const singleValue = props.isMulti ? undefined : props.value;
  const selected =
    singleValue == null
      ? undefined
      : props.options?.find((o) => o.value === singleValue && !o.isSeparator);

  const getUniqueLabel = (label: string, value: string) => {
    const matchingOptions = props.options?.filter((o) => o.label === label);
    if (matchingOptions && matchingOptions.length > 1) {
      return `${label}-${value}`;
    }
    return label;
  };
  const selectedOptions = props.isMulti
    ? (props.options ?? []).filter(
        (option) => !option.isSeparator && selectedValues.has(option.value),
      )
    : [];
  const triggerLabel = props.isMulti
    ? getMultiSelectLabel(selectedOptions, fallbackLabel)
    : (selected?.label ?? fallbackLabel);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="field"
          role="combobox"
          aria-expanded={open}
          disabled={props.disabled}
          className={cn(
            "w-full",
            !selected && !selectedOptions.length && "text-muted-foreground",
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {triggerLabel}
          </span>

          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          {props.showSearch !== false &&
            props.options &&
            props.options.length > 0 && (
              <CommandInput
                placeholder={props.placeholder ?? t(($) => $.commons.search)}
                className="h-9"
              />
            )}
          <CommandList>
            <CommandEmpty>{t(($) => $.commons.noItems)}</CommandEmpty>
            <CommandGroup>
              {props.options?.map((option) =>
                option.isSeparator ? (
                  <CommandSeparator key={option.value} />
                ) : (
                  <CommandItem
                    key={option.value}
                    value={getUniqueLabel(option.label, option.value)}
                    keywords={[option.label]}
                    disabled={option.disabled}
                    onSelect={() => {
                      if (props.isMulti) {
                        const nextSelectedValues = selectedValues.has(
                          option.value,
                        )
                          ? Array.from(selectedValues).filter(
                              (entry) => entry !== option.value,
                            )
                          : [...Array.from(selectedValues), option.value];
                        props.onChange(nextSelectedValues);
                        return;
                      }
                      props.onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    {props.isMulti && (
                      <span
                        className={cn(
                          "flex size-4 items-center justify-center rounded-sm border",
                          selectedValues.has(option.value)
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-input bg-background",
                        )}
                      >
                        <Check
                          className={cn(
                            "size-3",
                            selectedValues.has(option.value)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                      </span>
                    )}
                    {option.label}
                    <Check
                      className={cn(
                        !props.isMulti && singleValue === option.value
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ),
              )}
              {props.options?.length === 0 && (
                <CommandItem disabled value="-1" className="italic">
                  {t(($) => $.commons.noItems)}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
